import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Download, Search, FileText, Printer } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass, glassLight, T, Badge, StatCard, EmptyState, Field } from '../ui';
import 'https://cdn.magicpatterns.com/uploads/6VHLBX9XSQjy9HjvYqPJBL/pasted-image.png';
from;
'../assets/logo';
interface Cliente {
  id: string;
  codigo: string;
  nombre: string;
  telefono: string;
  direccion: string;
  capacidadTanque: number;
  rnc?: string;
  representante?: string;
  representanteCedula?: string;
  representanteDireccion?: string;
}
interface Contrato {
  id: string;
  numeroContrato: string;
  clienteId: string;
  clienteNombre: string;
  fechaInicio: string;
  fechaVencimiento: string;
  precioAcordado: number;
  condiciones: string;
  estado: 'vigente' | 'vencido' | 'cancelado';
  firmaDigital?: string;
  fecha_creacion: string;
  cantidadTanques?: number;
  capacidadGalones?: number;
  valorTanque?: number;
  ciudad?: string;
}
interface ContratosProps {
  contratos: Contrato[];
  clientes: Cliente[];
  onAddContrato: (contrato: Contrato) => void;
  onDeleteContrato: (id: string) => void;
  onUpdateContrato: (contrato: Contrato) => void;
}
const estadoStyle = (estado: Contrato['estado']) => {
  if (estado === 'vigente') return {
    color: T.ok,
    soft: T.okSoft
  };
  if (estado === 'vencido') return {
    color: T.danger,
    soft: T.dangerSoft
  };
  return {
    color: T.textMuted,
    soft: T.surfaceSubtle
  };
};
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};
export function Contratos({
  contratos,
  clientes,
  onAddContrato,
  onDeleteContrato,
  onUpdateContrato
}: ContratosProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'vigente' | 'vencido' | 'cancelado'>('vigente');
  const [formData, setFormData] = useState<Partial<Contrato>>({
    estado: 'vigente',
    precioAcordado: 0,
    cantidadTanques: 1,
    ciudad: 'Santiago de los Caballeros'
  });
  const [selectedCliente, setSelectedCliente] = useState<string>('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.numeroContrato || !selectedCliente || !formData.fechaInicio || !formData.fechaVencimiento) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    const cliente = clientes.find(c => c.id === selectedCliente);
    if (editingId) {
      onUpdateContrato({
        ...formData,
        id: editingId,
        clienteId: selectedCliente,
        clienteNombre: cliente?.nombre || ''
      } as Contrato);
      setEditingId(null);
    } else {
      onAddContrato({
        ...formData,
        id: Date.now().toString(),
        clienteId: selectedCliente,
        clienteNombre: cliente?.nombre || '',
        fecha_creacion: new Date().toISOString().split('T')[0]
      } as Contrato);
    }
    setFormData({
      estado: 'vigente',
      precioAcordado: 0,
      cantidadTanques: 1,
      ciudad: 'Santiago de los Caballeros'
    });
    setSelectedCliente('');
    setShowForm(false);
  };
  const numeroALetras = (n: number) => {
    const map: Record<number, string> = {
      1: 'uno (1)',
      2: 'dos (2)',
      3: 'tres (3)',
      4: 'cuatro (4)',
      5: 'cinco (5)',
      6: 'seis (6)'
    };
    return map[n] || `${n} (${n})`;
  };
  const imprimirContrato = (contrato: Contrato) => {
    const cliente = clientes.find(c => c.id === contrato.clienteId);
    if (!cliente) {
      alert('No se encontró el cliente de este contrato.');
      return;
    }
    const ciudad = contrato.ciudad || 'Santiago de los Caballeros';
    const cantidad = contrato.cantidadTanques || 1;
    const capacidad = contrato.capacidadGalones || cliente.capacidadTanque || 0;
    const valor = contrato.valorTanque || 0;
    const fmtFecha = (iso: string) => {
      if (!iso) return '____________';
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('es-DO', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    };
    const fmtMoneda = (n: number) => new Intl.NumberFormat('es-DO', {
      minimumFractionDigits: 2
    }).format(n);
    const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Contrato ${contrato.numeroContrato}</title>
<style>
  @page { size: letter; margin: 2.2cm 2.4cm; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
  body { font-family: 'Georgia', 'Times New Roman', serif; font-size: 12.5pt; line-height: 1.5; color: #111; }
  h1 { text-align: center; font-size: 15pt; margin: 0 0 2px; letter-spacing: .02em; }
  h1.sub { margin-bottom: 22px; }
  p { text-align: justify; margin: 0 0 14px; }
  strong { font-weight: 700; }
  .clausula { margin-bottom: 14px; }
  .parrafo { margin: 6px 0 6px 18px; }
  .firmas { margin-top: 60px; display: flex; justify-content: space-between; gap: 40px; }
  .firma { flex: 1; text-align: center; }
  .firma .linea { border-top: 1px solid #111; margin-top: 50px; padding-top: 6px; font-size: 11pt; }
  .meta { text-align: right; font-size: 11pt; margin-bottom: 24px; }
  .letterhead { display: flex; align-items: center; gap: 12px; justify-content: center; margin-bottom: 10px; }
  .letterhead img { height: 46px; width: auto; }
  @media print {
    .no-print { display: none; }
  }
  .no-print { position: fixed; top: 14px; right: 14px; }
  .no-print button {
    background: #FF6B01; color: #fff; border: none; padding: 10px 18px;
    border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer;
    font-family: sans-serif;
  }
</style>
</head>
<body>
  <div class="no-print"><button onclick="window.print()">Imprimir / Guardar PDF</button></div>

  <div class="letterhead"><img src="${LOGO_DATA_URI}" alt="Junquito Gas" /></div>
  <div class="meta">${ciudad}, Rep. Dom., ${fmtFecha(contrato.fechaInicio)}</div>

  <h1>CONTRATO DE INSTALACIÓN DE CILINDROS</h1>
  <h1 class="sub">Y VENTA DE GLP INDUSTRIAL</h1>

  <p><strong>ENTRE:</strong> La compañía <strong>JUNQUITO GAS, SRL.</strong>, portadora del RNC
  1-30-16152-6, teléfono 809-247-2104, ubicada en la Av. Antonio Guzmán No. 102 de esta ciudad
  de Santiago de los Caballeros; quien en lo adelante y para fines y consecuencias legales del
  presente acto, se denominará <strong>LA EMPRESA</strong>.</p>

  <p><strong>Y DE LA OTRA PARTE</strong>, ${cliente.nombre}${cliente.rnc ? `, portador de RNC ${cliente.rnc}` : ''}${cliente.telefono ? `, teléfono ${cliente.telefono}` : ''}${cliente.direccion ? `, ubicado en ${cliente.direccion}` : ''}.${cliente.representante ? ` El mismo, propiedad del señor(a) <strong>${cliente.representante}</strong>, dominicano(a), mayor de edad${cliente.representanteCedula ? `, portador(a) de la cédula de identidad y electoral número ${cliente.representanteCedula}` : ''}${cliente.representanteDireccion ? `, domiciliado(a) en ${cliente.representanteDireccion}` : ''}.` : ''} En lo adelante y para fines y consecuencias legales del presente acto, se denominará
  <strong>EL CLIENTE</strong>.</p>

  <p style="text-align:center"><strong>SE HA CONVENIDO Y PACTADO LO SIGUIENTE:</strong></p>

  <div class="clausula"><p><strong>PRIMERO:</strong> LA EMPRESA instalará ${cantidad > 1 ? `${numeroALetras(cantidad)} cilindros` : 'un cilindro'} estacionario${cantidad > 1 ? 's' : ''} ${capacidad ? `de capacidad de <strong>(${capacidad})</strong> galones cada uno ` : ''}para el despacho de GLP, por pedido de EL CLIENTE, el cual debe realizarse con
  veinticuatro (24) horas de anticipación, para poder brindar un mejor servicio.</p></div>

  <div class="clausula"><p><strong>SEGUNDO:</strong> El/los cilindro(s) será(n) propiedad de LA EMPRESA,
  por lo que EL CLIENTE no podrá descargar de ningún otro proveedor. Para asegurar el
  cumplimiento de este requisito, LA EMPRESA contará con un sistema de seguridad que le
  permitirá monitorear las descargas.</p>
  <p class="parrafo"><strong>PÁRRAFO I:</strong> LA EMPRESA hace entrega de ${cantidad > 1 ? `${numeroALetras(cantidad)} cilindros` : 'un cilindro'}${capacidad ? ` con capacidad para ${capacidad} galones` : ''}${valor ? ` valorado${cantidad > 1 ? 's' : ''} en RD$ ${fmtMoneda(valor)} pesos` : ''}, este equipo será propiedad de LA EMPRESA. En caso de pérdida o deterioro EL CLIENTE debe
  pagarlo en su totalidad al precio del día en el mercado.</p>
  <p class="parrafo"><strong>PÁRRAFO II:</strong> Es responsabilidad de EL CLIENTE el cuidado y
  mantenimiento de todos los equipos instalados, así como el reemplazo de las piezas. El
  cilindro no podrá estar pegado a la tierra, sino que deberá tener una base que lo mantenga en
  buen estado; y cumplir con todos los requisitos de seguridad que exijan las autoridades
  nacionales y municipales.</p>
  <p class="parrafo"><strong>PÁRRAFO III:</strong> EL CLIENTE debe poseer un extintor de veinte
  (20) libras para ser usado en caso de emergencias. LA EMPRESA no es responsable ante
  cualquier accidente que pueda ocurrir por el mal manejo del equipo una vez instalado.</p></div>

  <div class="clausula"><p><strong>TERCERO:</strong> El precio de GLP se fijará conforme la
  RESOLUCIÓN SEMANAL del Ministerio de Industria y Comercio${contrato.precioAcordado ? ` (precio de referencia acordado: RD$ ${fmtMoneda(contrato.precioAcordado)})` : ''}.</p>
  <p class="parrafo"><strong>PÁRRAFO I:</strong> EL CLIENTE debe realizar el pago de la factura
  antes de hacer su próximo pedido.</p>
  <p class="parrafo"><strong>PÁRRAFO II:</strong> Las facturas no podrán exceder los treinta (30)
  días para ser pagadas, aunque EL CLIENTE no vaya a realizar un próximo pedido.</p></div>

  <div class="clausula"><p><strong>CUARTO:</strong> Este contrato tendrá vigencia a partir del día
  ${fmtFecha(contrato.fechaInicio)} y se vence de manera definitiva el día
  ${fmtFecha(contrato.fechaVencimiento)}.</p>
  <p class="parrafo"><strong>PÁRRAFO I:</strong> El presente contrato quedará automáticamente
  renovado por un nuevo período, en caso de que sesenta (60) días antes de la fecha de
  vencimiento ninguna de las partes notifique por escrito su deseo de no continuar.</p></div>

  <div class="clausula"><p><strong>QUINTO:</strong> En caso de que EL CLIENTE desee terminar el
  contrato antes de la fecha establecida, deberá comunicarlo por escrito a LA EMPRESA con
  quince (15) días de anticipación.</p></div>

  <div class="clausula"><p><strong>SEXTO:</strong> Las partes pactan que LA EMPRESA se reserva el
  derecho y la opción de renovar el presente contrato a la llegada de su término, situación que
  acepta y reconoce EL CLIENTE.</p></div>

  <div class="clausula"><p><strong>SÉPTIMO:</strong> Las partes hacen elección de domicilio en sus
  respectivas direcciones anteriormente descritas.</p></div>

  <div class="clausula"><p><strong>OCTAVO:</strong> LA EMPRESA se reserva el derecho de retirar
  los equipos instalados en el momento que lo considere necesario, sin incurrir en ninguna
  responsabilidad.</p></div>

  ${contrato.condiciones ? `<div class="clausula"><p><strong>CONDICIONES ADICIONALES:</strong> ${contrato.condiciones}</p></div>` : ''}

  <p>HECHO en dos originales de un mismo tenor y efecto, uno para cada una de las partes, en la
  ciudad de ${ciudad}, hoy ${fmtFecha(contrato.fechaInicio)}.</p>

  <div class="firmas">
    <div class="firma">
      <div class="linea">LA EMPRESA<br/>Junquito Gas, SRL.</div>
    </div>
    <div class="firma">
      <div class="linea">EL CLIENTE<br/>${cliente.representante || cliente.nombre}</div>
    </div>
  </div>
</body>
</html>`;
    const w = window.open('', '_blank');
    if (!w) {
      alert('El navegador bloqueó la ventana de impresión. Habilita las ventanas emergentes.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };
  const handleEdit = (contrato: Contrato) => {
    setFormData(contrato);
    setSelectedCliente(contrato.clienteId);
    setEditingId(contrato.id);
    setShowForm(true);
  };
  const filteredContratos = contratos.filter(c => {
    const matchSearch = c.numeroContrato.toLowerCase().includes(searchTerm.toLowerCase()) || c.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filterEstado === 'todos' || c.estado === filterEstado;
    return matchSearch && matchEstado;
  });
  const contratosVigentes = contratos.filter(c => c.estado === 'vigente').length;
  const contratosVencidos = contratos.filter(c => c.estado === 'vencido').length;
  const generatePDF = (contrato: Contrato) => {
    const cliente = clientes.find(c => c.id === contrato.clienteId);
    if (!cliente) {
      alert('No se encontró el cliente de este contrato.');
      return;
    }
    const es = estadoStyle(contrato.estado);
    const fmtFecha = (iso: string) => {
      if (!iso) return '—';
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString('es-DO', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    };
    const fmtMoneda = (n: number) => new Intl.NumberFormat('es-DO', {
      minimumFractionDigits: 2
    }).format(n);
    const seccion = (titulo: string) => `
      <tr class="seccion"><td colspan="2">${titulo}</td></tr>`;
    const fila = (label: string, value: string) => `
      <tr>
        <td class="celda-label">${label}</td>
        <td class="celda-valor">${value || '—'}</td>
      </tr>`;
    const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>Resumen ${contrato.numeroContrato}</title>
<style>
  @page { size: letter; margin: 0; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
  body {
    margin: 0; font-family: 'DM Sans', -apple-system, Helvetica, Arial, sans-serif;
    color: #1A1207; background: #fff;
  }
  .sheet { max-width: 850px; margin: 0 auto; }
  .hero {
    background: radial-gradient(120% 160% at 15% 0%, #2A1000 0%, #150900 55%, #0D0600 100%);
    color: #FFF7EE; padding: 30px 44px; position: relative; overflow: hidden;
  }
  .hero::before {
    content: ''; position: absolute; top: -80px; right: -60px; width: 260px; height: 260px;
    border-radius: 50%; background: radial-gradient(circle, rgba(255,107,1,.35) 0%, transparent 70%);
  }
  .hero-top { display: flex; align-items: center; justify-content: space-between; position: relative; }
  .brand { display: flex; align-items: center; gap: 18px; }
  .brand img { height: 84px; width: auto; }
  .brand-name { font-family: 'Space Grotesk', sans-serif; font-weight: 800; font-size: 20px; }
  .brand-sub { font-size: 10.5px; letter-spacing: .12em; text-transform: uppercase; color: rgba(255,220,180,.7); margin-top: 2px; }
  .doc-tag {
    font-size: 10px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase;
    color: rgba(255,220,180,.75); text-align: right;
  }
  h1 { font-family: 'Space Grotesk', sans-serif; font-size: 24px; font-weight: 800; margin: 20px 0 4px; position: relative; }
  .contract-no { font-size: 13px; color: rgba(255,247,238,.75); position: relative; }
  .badge {
    display: inline-block; margin-top: 12px; padding: 5px 14px; border-radius: 99px;
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em;
    background: ${es.soft}; color: ${es.color}; position: relative;
  }
  .body { padding: 30px 44px 0; }
  table.detalle { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 22px; }
  table.detalle td { padding: 10px 4px; border-bottom: 1px solid rgba(26,18,7,.08); }
  table.detalle tr.seccion td {
    padding: 16px 4px 6px; border-bottom: 2px solid #FF6B01; font-size: 11px;
    font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #FF6B01;
  }
  .celda-label { color: rgba(26,18,7,.55); width: 42%; }
  .celda-valor { font-weight: 700; text-align: right; }
  .total-card {
    background: linear-gradient(135deg, #FF8C00 0%, #FF5A00 100%); color: #fff; border-radius: 14px;
    padding: 20px 24px; display: flex; align-items: center; justify-content: space-between;
    box-shadow: 0 10px 28px rgba(255,107,1,.28); margin-bottom: 22px;
  }
  .total-card .label { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; opacity: .85; }
  .total-card .value { font-family: 'Space Grotesk', sans-serif; font-size: 27px; font-weight: 800; }
  .condiciones {
    border: 1px solid rgba(26,18,7,.12); border-radius: 12px; padding: 16px 20px;
    font-size: 13px; line-height: 1.6; color: rgba(26,18,7,.8); margin-bottom: 26px;
  }
  .condiciones h3 { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #FF6B01; margin: 0 0 10px; }
  footer {
    padding: 18px 44px; border-top: 1px solid rgba(26,18,7,.1); font-size: 11px;
    color: rgba(26,18,7,.5); display: flex; justify-content: space-between;
  }
  .no-print { position: fixed; top: 14px; right: 14px; }
  .no-print button {
    background: #FF6B01; color: #fff; border: none; padding: 10px 18px;
    border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer;
    font-family: sans-serif; box-shadow: 0 6px 18px rgba(255,107,1,.35);
  }
  @media print { .no-print { display: none; } }
</style>
</head>
<body>
  <div class="no-print"><button onclick="window.print()">Imprimir / Guardar PDF</button></div>
  <div class="sheet">
    <div class="hero">
      <div class="hero-top">
        <div class="brand">
          <img src="${LOGO_DATA_URI}" alt="Junquito Gas" />
          <div>
            <div class="brand-name">Junquito Gas, SRL.</div>
            <div class="brand-sub">Gestión comercial · GLP industrial</div>
          </div>
        </div>
        <div class="doc-tag">Resumen de contrato</div>
      </div>
      <h1>Contrato ${contrato.numeroContrato}</h1>
      <div class="contract-no">Cliente: ${cliente.nombre}</div>
      <span class="badge">${contrato.estado}</span>
    </div>

    <div class="body">
      <table class="detalle">
        ${seccion('Datos del cliente')}
        ${fila('Nombre / razón social', cliente.nombre)}
        ${fila('RNC', cliente.rnc || '—')}
        ${fila('Teléfono', cliente.telefono)}
        ${fila('Dirección', cliente.direccion)}
        ${fila('Representante', cliente.representante || '—')}
        ${fila('Cédula del representante', cliente.representanteCedula || '—')}

        ${seccion('Datos del contrato')}
        ${fila('Número de contrato', contrato.numeroContrato)}
        ${fila('Ciudad', contrato.ciudad || 'Santiago de los Caballeros')}
        ${fila('Fecha de inicio', fmtFecha(contrato.fechaInicio))}
        ${fila('Fecha de vencimiento', fmtFecha(contrato.fechaVencimiento))}
        ${fila('Estado', contrato.estado)}

        ${seccion('Cilindro(s) instalado(s)')}
        ${fila('Cantidad de cilindros', String(contrato.cantidadTanques || 1))}
        ${fila('Capacidad', `${contrato.capacidadGalones || cliente.capacidadTanque || '—'} galones`)}
        ${fila('Valor del cilindro', contrato.valorTanque ? `RD$ ${fmtMoneda(contrato.valorTanque)}` : '—')}
      </table>

      <div class="total-card">
        <span class="label">Precio acordado</span>
        <span class="value">RD$ ${fmtMoneda(contrato.precioAcordado || 0)}</span>
      </div>

      ${contrato.condiciones ? `<div class="condiciones"><h3>Condiciones comerciales</h3>${contrato.condiciones}</div>` : ''}
    </div>

    <footer>
      <span>Junquito Gas, SRL. · RNC 1-30-16152-6 · 809-247-2104</span>
      <span>Generado el ${new Date().toLocaleDateString('es-DO')}</span>
    </footer>
  </div>
</body>
</html>`;
    const w = window.open('', '_blank');
    if (!w) {
      alert('El navegador bloqueó la ventana. Habilita las ventanas emergentes.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
  };
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  }} data-id="element-390">
      {/* Header Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 14
    }} data-id="element-391">
        
        <StatCard label="Total contratos" value={String(contratos.length)} note="Registrados en el sistema" accent={T.info} data-id="element-392" />
        
        <StatCard label="Vigentes" value={String(contratosVigentes)} note="Activos hoy" accent={T.ok} data-id="element-393" />
        
        <StatCard label="Vencidos" value={String(contratosVencidos)} note="Requieren renovación" accent={T.danger} data-id="element-394" />
        
      </div>

      {/* Controles */}
      <div style={{
      ...glassLight,
      padding: 16
    }} data-id="element-395">
        <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center'
      }} data-id="element-396">
          
          <div style={{
          flex: '1 1 240px',
          position: 'relative'
        }} data-id="element-397">
            <Search size={16} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-398" />
            
            <input type="text" placeholder="Buscar por número o cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 36
          }} data-id="element-399" />
            
          </div>
          <select value={filterEstado} onChange={e => setFilterEstado(e.target.value as any)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-400">
            
            <option value="todos" data-id="element-401">Todos</option>
            <option value="vigente" data-id="element-402">Vigentes</option>
            <option value="vencido" data-id="element-403">Vencidos</option>
            <option value="cancelado" data-id="element-404">Cancelados</option>
          </select>
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            estado: 'vigente',
            precioAcordado: 0,
            cantidadTanques: 1,
            ciudad: 'Santiago de los Caballeros'
          });
          setSelectedCliente('');
        }} style={{
          ...primaryButton,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-405">
            
            <Plus size={15} data-id="element-406" /> Nuevo Contrato
          </button>
        </div>
      </div>

      {/* Formulario */}
      <AnimatePresence data-id="element-407">
        {showForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} style={{
        overflow: 'hidden'
      }} data-id="element-408">
          
            <div style={{
          ...glass,
          padding: 20
        }} data-id="element-409">
              <h3 style={{
            fontSize: 15,
            fontWeight: 800,
            marginBottom: 16,
            color: T.text
          }} data-id="element-410">
              
                {editingId ? 'Editar Contrato' : 'Nuevo Contrato'}
              </h3>
              <form onSubmit={handleSubmit} data-id="element-411">
                <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14
            }} data-id="element-412">
                
                  <Field label="Número de contrato *" data-id="element-413">
                    <input type="text" value={formData.numeroContrato || ''} onChange={e => setFormData({
                  ...formData,
                  numeroContrato: e.target.value
                })} style={inputStyle} placeholder="CTR-2024-001" data-id="element-414" />
                  
                  </Field>
                  <Field label="Cliente *" data-id="element-415">
                    <select value={selectedCliente} onChange={e => setSelectedCliente(e.target.value)} style={inputStyle} data-id="element-416">
                    
                      <option value="" data-id="element-417">Selecciona un cliente</option>
                      {clientes.map(c => <option key={c.id} value={c.id} data-id="element-418">
                          {c.nombre} ({c.codigo})
                        </option>)}
                    </select>
                  </Field>
                  <Field label="Fecha de inicio *" data-id="element-419">
                    <input type="date" value={formData.fechaInicio || ''} onChange={e => setFormData({
                  ...formData,
                  fechaInicio: e.target.value
                })} style={inputStyle} data-id="element-420" />
                  
                  </Field>
                  <Field label="Fecha de vencimiento *" data-id="element-421">
                    <input type="date" value={formData.fechaVencimiento || ''} onChange={e => setFormData({
                  ...formData,
                  fechaVencimiento: e.target.value
                })} style={inputStyle} data-id="element-422" />
                  
                  </Field>
                  <Field label="Precio acordado (RD$)" data-id="element-423">
                    <input type="number" step="0.01" value={formData.precioAcordado || 0} onChange={e => setFormData({
                  ...formData,
                  precioAcordado: parseFloat(e.target.value)
                })} style={inputStyle} placeholder="0.00" data-id="element-424" />
                  
                  </Field>
                  <Field label="Cantidad de cilindros" data-id="element-425">
                    <input type="number" min={1} value={formData.cantidadTanques ?? 1} onChange={e => setFormData({
                  ...formData,
                  cantidadTanques: parseInt(e.target.value) || 1
                })} style={inputStyle} data-id="element-426" />
                  
                  </Field>
                  <Field label="Capacidad del cilindro (galones)" data-id="element-427">
                    <input type="number" value={formData.capacidadGalones ?? ''} onChange={e => setFormData({
                  ...formData,
                  capacidadGalones: parseInt(e.target.value) || undefined
                })} style={inputStyle} placeholder="120 / 250 / 500" data-id="element-428" />
                  
                  </Field>
                  <Field label="Valor del cilindro (RD$)" data-id="element-429">
                    <input type="number" step="0.01" value={formData.valorTanque ?? ''} onChange={e => setFormData({
                  ...formData,
                  valorTanque: parseFloat(e.target.value) || undefined
                })} style={inputStyle} placeholder="140000.00" data-id="element-430" />
                  
                  </Field>
                  <Field label="Ciudad del contrato" data-id="element-431">
                    <input type="text" value={formData.ciudad || 'Santiago de los Caballeros'} onChange={e => setFormData({
                  ...formData,
                  ciudad: e.target.value
                })} style={inputStyle} data-id="element-432" />
                  
                  </Field>
                  <Field label="Estado" data-id="element-433">
                    <select value={formData.estado || 'vigente'} onChange={e => setFormData({
                  ...formData,
                  estado: e.target.value as any
                })} style={inputStyle} data-id="element-434">
                    
                      <option value="vigente" data-id="element-435">Vigente</option>
                      <option value="vencido" data-id="element-436">Vencido</option>
                      <option value="cancelado" data-id="element-437">Cancelado</option>
                    </select>
                  </Field>
                  <div style={{
                gridColumn: '1 / -1'
              }} data-id="element-438">
                    <Field label="Condiciones comerciales" data-id="element-439">
                      <textarea value={formData.condiciones || ''} onChange={e => setFormData({
                    ...formData,
                    condiciones: e.target.value
                  })} style={{
                    ...inputStyle,
                    resize: 'vertical'
                  }} rows={4} placeholder="Especifica las condiciones del contrato..." data-id="element-440" />
                    
                    </Field>
                  </div>
                </div>
                <div style={{
              display: 'flex',
              gap: 8,
              marginTop: 16
            }} data-id="element-441">
                  <button type="submit" style={primaryButton} data-id="element-442">
                    {editingId ? 'Actualizar' : 'Crear'} contrato
                  </button>
                  <button type="button" onClick={() => {
                setShowForm(false);
                setEditingId(null);
              }} style={ghostButton} data-id="element-443">
                  
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Lista de Contratos */}
      <div style={{
      ...glass,
      padding: 20
    }} data-id="element-444">
        <h3 style={{
        fontSize: 15,
        fontWeight: 800,
        marginBottom: 16,
        color: T.text
      }} data-id="element-445">
          
          Contratos ({filteredContratos.length})
        </h3>
        {filteredContratos.length === 0 ? <EmptyState icon={<FileText size={20} data-id="element-447" />} title="No hay contratos" hint="Crea un contrato nuevo o ajusta los filtros de búsqueda." data-id="element-446" /> : <div style={{
        overflowX: 'auto'
      }} data-id="element-448">
            <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13
        }} data-id="element-449">
              <thead data-id="element-450">
                <tr data-id="element-451">
                  {['Nº Contrato', 'Cliente', 'Inicio', 'Vencimiento', 'Precio (RD$)', 'Estado', 'Acciones'].map((h, i) => <th key={h} style={{
                textAlign: i === 4 ? 'right' : i === 6 ? 'center' : 'left',
                padding: '0 10px 10px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: T.textMuted,
                borderBottom: `1px solid ${T.border}`
              }} data-id="element-452">
                  
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-453">
                {filteredContratos.map(contrato => {
              const es = estadoStyle(contrato.estado);
              return <tr key={contrato.id} data-id="element-454">
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  fontFamily: 'monospace',
                  color: T.info,
                  fontWeight: 700
                }} data-id="element-455">
                      
                        {contrato.numeroContrato}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  fontWeight: 600,
                  color: T.text
                }} data-id="element-456">
                      
                        {contrato.clienteNombre}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-457">
                      
                        {contrato.fechaInicio}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  color: T.textSecondary
                }} data-id="element-458">
                      
                        {contrato.fechaVencimiento}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'right',
                  fontWeight: 700,
                  color: T.text
                }} data-id="element-459">
                      
                        {contrato.precioAcordado.toFixed(2)}
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`
                }} data-id="element-460">
                      
                        <Badge label={contrato.estado} color={es.color} soft={es.soft} data-id="element-461" />
                      </td>
                      <td style={{
                  padding: '12px 10px',
                  borderBottom: `1px solid ${T.border}`,
                  textAlign: 'center'
                }} data-id="element-462">
                      
                        <div style={{
                    display: 'inline-flex',
                    gap: 6,
                    justifyContent: 'center'
                  }} data-id="element-463">
                        
                          <button onClick={() => imprimirContrato(contrato)} title="Imprimir contrato" style={{
                      ...iconBtn,
                      color: '#8B5CF6'
                    }} data-id="element-464">
                          
                            <Printer size={14} data-id="element-465" />
                          </button>
                          <button onClick={() => generatePDF(contrato)} title="Ver resumen en PDF" style={{
                      ...iconBtn,
                      color: T.ok
                    }} data-id="element-466">
                          
                            <Download size={14} data-id="element-467" />
                          </button>
                          <button onClick={() => handleEdit(contrato)} title="Editar" style={{
                      ...iconBtn,
                      color: T.info
                    }} data-id="element-468">
                          
                            <Edit2 size={14} data-id="element-469" />
                          </button>
                          <button onClick={() => onDeleteContrato(contrato.id)} title="Eliminar" style={{
                      ...iconBtn,
                      color: T.danger
                    }} data-id="element-470">
                          
                            <Trash2 size={14} data-id="element-471" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}